import { ApiError } from './api-client';

export interface FormErrorPresentation {
  /** Banner text. */
  message: string;
  /** Per-input messages, keyed by form field name. */
  fields: Record<string, string>;
  /** True when the email or mobile is already registered — offer a sign-in link. */
  accountExists: boolean;
}

const ALREADY_REGISTERED = 'Already registered — try another, or sign in';

/**
 * Turns an api rejection into something a form can render.
 *
 * The api reports validation failures as `error.details`, an array of
 * `{ path, message }`. Mapping those onto inputs is what makes a server-side
 * rejection visible where it happened rather than only in a banner.
 */
export function presentApiError(caught: unknown, fallback: string): FormErrorPresentation {
  if (!(caught instanceof ApiError)) {
    return {
      message: caught instanceof Error ? caught.message : fallback,
      fields: {},
      accountExists: false,
    };
  }

  if (caught.code === 'ACCOUNT_EXISTS') {
    return {
      message: 'An account with that email or mobile already exists.',
      fields: { email: ALREADY_REGISTERED, phone: ALREADY_REGISTERED },
      accountExists: true,
    };
  }

  const details = Array.isArray(caught.details)
    ? (caught.details as Array<{ path?: unknown; message?: unknown }>)
    : [];

  const fields: Record<string, string> = {};
  for (const detail of details) {
    if (typeof detail?.path !== 'string' || typeof detail.message !== 'string') continue;
    // Nested paths arrive dotted (`availability.days`); forms key off the root.
    const field = detail.path.split('.')[0];
    if (field && !fields[field]) fields[field] = detail.message;
  }

  return {
    message: caught.message || fallback,
    fields,
    accountExists: false,
  };
}
